import type { AdminRole } from 'restaurantAdmin/auth/apiHelpers';
import { useAuth } from 'restaurantAdmin/context/AuthContext';
import { Field } from '@base-ui/react';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormSelect } from '@components/formInputs/ControlledFormSelect';
import { Switch } from '@components/switch/Switch';
import cx from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { SettingsOutletHeader } from '../shared/SettingsOutletHeader';
import typography from '~styles/typography.scss';
import styles from './EditTeamMemberPage.scss';
import { useFetchAdmin, useUpdateAdminMutation } from './hooks';

export interface EditTeamMemberFormData {
  role: AdminRole;
  reservationEmailsEnabled: boolean;
}

export const EditTeamMemberPage = () => {
  const { adminId: authenticatedAdminId } = useAuth();
  const { adminId } = useParams();
  const navigate = useNavigate();
  const { data: admin, isLoading } = useFetchAdmin();
  const updateAdminMutation = useUpdateAdminMutation();

  const { control, handleSubmit } = useForm<EditTeamMemberFormData>({
    values: {
      role: admin?.role || 'host',
      reservationEmailsEnabled: admin?.reservationEmailsEnabled || false,
    },
  });

  if (isLoading || !adminId) {
    return null;
  }

  const goBack = () => navigate(-1);

  const ROLE_OPTIONS = [
    {
      label: 'Host',
      value: 'host',
    },
    {
      label: 'Manager',
      value: 'manager',
    },
  ];

  return (
    <div>
      <SettingsOutletHeader title="Edit Team Member" />
      <form
        className={styles.form}
        onSubmit={handleSubmit((data) => updateAdminMutation.mutate(data))}
      >
        <fieldset>
          <section>
            <div className={styles.labelContainer}>
              <h2 className={typography.h5}>Member Info</h2>
            </div>
            <div className={styles.fieldContainer}>
              <div className={styles.memberInfoBox}>
                <h6 className={typography.c1}>{admin!.fullName}</h6>
                <p className={typography.c2_20}>{admin!.email}</p>
              </div>
            </div>
          </section>
          <section>
            <div className={styles.labelContainer}>
              <h2 className={typography.h5}>Role</h2>
              <p className={typography.t2}>
                Hosts can only access Service and Guest Book.
              </p>
              <p className={typography.t2}>
                Managers can access all pages in Peak.
              </p>
            </div>
            <div className={styles.fieldContainer}>
              <ControlledFormSelect
                disabled={adminId === authenticatedAdminId}
                control={control}
                defaultValue={admin!.role}
                options={ROLE_OPTIONS}
                label="Role"
                name="role"
              />
            </div>
          </section>
          <section>
            <div className={styles.labelContainer}>
              <h2 className={typography.h5}>Notifications</h2>
            </div>
            <div className={styles.fieldContainer}>
              <Controller
                control={control}
                name="reservationEmailsEnabled"
                render={({ field: { value, onChange } }) => (
                  <Field.Root>
                    <div className={styles.emailPreference}>
                      <Field.Label className={typography.c2_20}>
                        Reservation Booked
                      </Field.Label>
                      <Switch checked={value} onCheckedChange={onChange} />
                    </div>
                    <Field.Description
                      className={cx(typography.t2, styles.description)}
                    >
                      Receive emails whenever guests book reservations.
                    </Field.Description>
                  </Field.Root>
                )}
              />
            </div>
          </section>
          <section>
            <div className={styles.actions}>
              <Button
                variant={ButtonVariants.Tertiary}
                label="Cancel"
                onClick={goBack}
              />
              <Button
                variant={ButtonVariants.Primary}
                label="Save"
                type="submit"
              />
            </div>
          </section>
        </fieldset>
      </form>
    </div>
  );
};
